import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useTabParam } from "../../lib/useTabParam";
import type { Deal } from "../../lib/types";
import { fmtInt, fmtPct } from "../../lib/analytics";

import DealLink from "../DealLink";
import DealAgeChips from "../shared/DealAgeChips";
import { daysInStage } from "../../lib/dealAge";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import { fetchDealNeighborhoodOutcomes, type DealNeighborhoodOutcomes } from "../../lib/neighborhoodClaims";
import { NeighborhoodInsuranceLine } from "../claims/NeighborhoodInsurance";

/** Jennifer's QC & Reinspection Board.
 *  5 tabs: Pipeline / Schedule / Report QC Queue / Reinspections / SLA Dashboard.
 *  (Warranty moved to Dispatch Board on May 9, 2026 — Robert + Jennifer drive
 *  warranty work from Dispatch now. See dispatch/WarrantyTab.tsx.) */

type TabKey = "qc" | "reinspections" | "sla";

// Pipeline (→ My Pipeline) and Company Schedule (→ Home schedule) removed
// Jun 16-17, 2026 — replaced by their dedicated surfaces.
const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "qc", label: "Report QC Queue" },
  { key: "reinspections", label: "Reinspections" },
  { key: "sla", label: "SLA Dashboard" },
];

const TAB_KEYS = TABS.map((t) => t.key) as readonly TabKey[];

export default function QCBoard() {
  const [tab, setTab] = useTabParam<TabKey>("tab", "qc", TAB_KEYS);
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-bardin-ink">
          QC &amp; Reinspection Board
        </h1>
        <p className="text-sm text-slate-500">
          Report quality control, reinspection pipeline, SLA health.
        </p>
      </div>
      <div className="border-b border-slate-200 mb-4">
        <nav className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                "px-4 py-2 text-sm font-medium rounded-t -mb-px border-b-2 " +
                (tab === t.key
                  ? "text-bardin-teal border-bardin-teal"
                  : "text-slate-500 border-transparent hover:text-slate-700")
              }
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>
      {tab === "qc" && <QCQueueTab />}
      {tab === "reinspections" && <ReinspectionsTab />}
      {tab === "sla" && <SLATab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline — all company deals (Jennifer gets closing tab)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Report QC Queue — deals at qc_review AND report_approved stages
// ---------------------------------------------------------------------------

type QCDeal = Pick<
  Deal,
  | "id"
  | "homeowner_name"
  | "property_street"
  | "property_city"
  | "property_state"
  | "property_zip"
  | "claim_number"
  | "insurance_company"
  | "days_in_stage"
  | "stage_entered_at"
  | "phase_entered_at"
  | "created_at"
  | "report_qc_status"
  | "qc_rejection_count"
  | "stage"
  | "owner_id"
  | "split_rm_id"
  | "imging_report_link"
>;

interface DocLink {
  deal_id: string;
  file_type: string;
  storage_path: string;
  file_name: string;
}

function QCQueueTab() {
  const { member } = useAuth();
  const [qcDeals, setQcDeals] = useState<QCDeal[] | null>(null);
  const [approvedDeals, setApprovedDeals] = useState<QCDeal[] | null>(null);
  const [docs, setDocs] = useState<DocLink[]>([]);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());

  // Editable fields for report_approved cards
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editOwner, setEditOwner] = useState("");
  const [editSplit, setEditSplit] = useState("");
  const [editCanvasser, setEditCanvasser] = useState("");

  // Members list for dropdowns
  const [rosterList, setRosterList] = useState<Array<{ id: string; full_name: string; role: string }>>([]);

  const load = useCallback(async () => {
    const stageFilter = "stage.eq.qc_review,stage.eq.report_approved";
    const [{ data, error }, { data: roster }, { data: docData }] = await Promise.all([
      supabase
        .from("deals")
        .select(
          "id,homeowner_name,property_street,property_city,property_state,property_zip,claim_number,insurance_company,days_in_stage,stage_entered_at,phase_entered_at,created_at,report_qc_status,qc_rejection_count,stage,owner_id,split_rm_id,imging_report_link",
        )
        .or(stageFilter)
        .order("stage_entered_at", { ascending: true }),
      supabase.from("team_roster").select("id,full_name,role,email").eq("is_active", true),
      supabase
        .from("deal_documents")
        .select("deal_id,file_type,storage_path,file_name")
        .in("file_type", ["drone_report", "sr_weather_report", "noaa_weather_report", "storm_report"]),
    ]);

    if (error) { setErr(error.message); return; }

    const m: Record<string, string> = {};
    const rList: Array<{ id: string; full_name: string; role: string }> = [];
    (roster ?? []).forEach((r: Record<string, unknown>) => {
      m[r.id as string] = r.full_name as string;
      rList.push({ id: r.id as string, full_name: r.full_name as string, role: r.role as string });
    });
    setMembers(m);
    setRosterList(rList);
    setDocs((docData ?? []) as DocLink[]);

    const all = (data ?? []) as QCDeal[];
    setQcDeals(all.filter((d) => d.stage === "qc_review"));
    setApprovedDeals(all.filter((d) => d.stage === "report_approved"));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Get doc links for a deal
  const getDocsForDeal = (dealId: string, type: string) =>
    docs.filter((d) => d.deal_id === dealId && d.file_type === type);

  // Actions
  const passToApproved = async (dealId: string) => {
    setBusy((prev) => new Set(prev).add(dealId));
    try {
      await supabase.from("deals").update({
        stage: "report_approved",
        report_qc_status: "approved",
        report_qc_reviewed_at: new Date().toISOString(),
        days_in_stage: 0,
      }).eq("id", dealId);
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "stage_change",
        title: "QC Passed → Report Approved",
        description: `Report approved by ${member?.full_name ?? "QC"}`,
        metadata: { from: "qc_review", to: "report_approved" },
      });
      await load();
    } finally {
      setBusy((prev) => { const n = new Set(prev); n.delete(dealId); return n; });
    }
  };

  const markInsufficientDamage = async (dealId: string) => {
    setBusy((prev) => new Set(prev).add(dealId));
    try {
      await supabase.from("deals").update({
        stage: "insufficient_damage",
        days_in_stage: 0,
      }).eq("id", dealId);
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "stage_change",
        title: "Insufficient Damage",
        description: `Flagged as insufficient damage by ${member?.full_name ?? "QC"}. Joy to contact homeowner.`,
        metadata: { from: "qc_review", to: "insufficient_damage" },
      });
      await load();
    } finally {
      setBusy((prev) => { const n = new Set(prev); n.delete(dealId); return n; });
    }
  };

  const convertToLead = async (dealId: string) => {
    setBusy((prev) => new Set(prev).add(dealId));
    try {
      await supabase.from("deals").update({
        stage: "initial_contact",
        phase: "leads",
        days_in_stage: 0,
      }).eq("id", dealId);
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "stage_change",
        title: "Converted to Lead",
        description: `Converted from report_approved to initial_contact by ${member?.full_name ?? "ASM"}`,
        metadata: { from: "report_approved", to: "initial_contact" },
      });
      await load();
    } finally {
      setBusy((prev) => { const n = new Set(prev); n.delete(dealId); return n; });
    }
  };

  const saveEdit = async (dealId: string) => {
    setBusy((prev) => new Set(prev).add(dealId));
    try {
      const updates: Record<string, unknown> = {};
      if (editOwner) updates.owner_id = editOwner;
      if (editSplit) updates.split_rm_id = editSplit === "__none__" ? null : editSplit;
      if (editCanvasser !== undefined) updates.canvasser_name = editCanvasser || null;
      if (Object.keys(updates).length > 0) {
        await supabase.from("deals").update(updates).eq("id", dealId);
      }
      setEditingId(null);
      await load();
    } finally {
      setBusy((prev) => { const n = new Set(prev); n.delete(dealId); return n; });
    }
  };

  // Build signed URLs for docs
  const openDoc = async (storagePath: string) => {
    const { data } = await supabase.storage.from("deal-documents").createSignedUrl(storagePath, 600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };

  if (err) return <Panel error>Couldn&apos;t load QC queue — {err}</Panel>;
  if (!qcDeals || !approvedDeals) return <Panel>Loading QC queue…</Panel>;

  const rmList = rosterList.filter((r) =>
    ["restore_manager", "team_lead", "asm", "canvasser"].includes(r.role),
  );

  return (
    <div className="space-y-6">
      {/* ── QC Review Section ── */}
      <Panel>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-bardin-ink">
            Reports Awaiting QC (24-hr SLA)
          </h3>
          <span className="text-xs text-slate-500">{fmtInt(qcDeals.length)} in queue</span>
        </div>
        {qcDeals.length === 0 ? (
          <Empty>No reports waiting. Nice.</Empty>
        ) : (
          <div className="space-y-3">
            {qcDeals.map((d) => {
              const overdue = daysInStage(d) >= 1;
              const droneReports = getDocsForDeal(d.id, "drone_report");
              const srWeather = getDocsForDeal(d.id, "sr_weather_report");
              const noaaWeather = getDocsForDeal(d.id, "noaa_weather_report");
              const stormReports = getDocsForDeal(d.id, "storm_report");
              const isBusy = busy.has(d.id);

              return (
                <div
                  key={d.id}
                  className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition"
                >
                  {/* Header row */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-semibold text-bardin-ink">
                        <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {d.property_street ? (
                          isAddressLinkable(d) ? (
                            <a
                              href={mapsHref(d)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="hover:underline hover:text-slate-700"
                            >
                              {d.property_street}
                            </a>
                          ) : (
                            d.property_street
                          )
                        ) : null}
                        {d.claim_number ? ` · #${d.claim_number}` : ""}
                        {(d.qc_rejection_count ?? 0) > 0 && (
                          <span className="ml-2 text-amber-700 font-medium">
                            Rejected {d.qc_rejection_count}×
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <div
                        className={
                          "text-xs px-2 py-0.5 rounded " +
                          (overdue ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700")
                        }
                      >
                        {daysInStage(d)}d in queue
                      </div>
                      <DealAgeChips deal={d} align="right" />
                    </div>
                  </div>

                  {/* Insurance + Document links */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {d.insurance_company && (
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                        {d.insurance_company}
                      </span>
                    )}
                    {d.imging_report_link && (
                      <a
                        href={d.imging_report_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-bardin-teal hover:underline font-medium"
                      >
                        IMGING Report →
                      </a>
                    )}
                    {droneReports.map((doc, i) => (
                      <button
                        key={i}
                        onClick={() => openDoc(doc.storage_path)}
                        className="text-xs text-bardin-teal hover:underline font-medium"
                      >
                        Drone Report{droneReports.length > 1 ? ` ${i + 1}` : ""} →
                      </button>
                    ))}
                    {srWeather.map((doc, i) => (
                      <button
                        key={`sr-${i}`}
                        onClick={() => openDoc(doc.storage_path)}
                        className="text-xs text-indigo-600 hover:underline font-medium"
                      >
                        SR Weather →
                      </button>
                    ))}
                    {noaaWeather.map((doc, i) => (
                      <button
                        key={`noaa-${i}`}
                        onClick={() => openDoc(doc.storage_path)}
                        className="text-xs text-indigo-600 hover:underline font-medium"
                      >
                        NOAA Weather →
                      </button>
                    ))}
                    {stormReports.map((doc, i) => (
                      <button
                        key={`storm-${i}`}
                        onClick={() => openDoc(doc.storage_path)}
                        className="text-xs text-indigo-600 hover:underline font-medium"
                      >
                        Storm Report →
                      </button>
                    ))}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => passToApproved(d.id)}
                      disabled={isBusy}
                      className="px-4 py-1.5 text-xs font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-50"
                    >
                      {isBusy ? "…" : "✓ Pass"}
                    </button>
                    <button
                      onClick={() => markInsufficientDamage(d.id)}
                      disabled={isBusy}
                      className="px-4 py-1.5 text-xs font-semibold rounded bg-red-100 text-red-700 hover:bg-red-200 transition disabled:opacity-50"
                    >
                      {isBusy ? "…" : "✕ Insufficient Damage"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* ── Report Approved Section (ASM assigns & converts) ── */}
      <Panel>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-bardin-ink">
            Report Approved — Ready for ASM to Convert
          </h3>
          <span className="text-xs text-slate-500">{fmtInt(approvedDeals.length)} awaiting conversion</span>
        </div>
        {approvedDeals.length === 0 ? (
          <Empty>No approved reports waiting for conversion.</Empty>
        ) : (
          <div className="space-y-3">
            {approvedDeals.map((d) => {
              const isBusy = busy.has(d.id);
              const isEditing = editingId === d.id;
              const addr = [d.property_street, d.property_city, d.property_state, d.property_zip]
                .filter(Boolean)
                .join(", ");

              return (
                <div
                  key={d.id}
                  className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-semibold text-bardin-ink">
                        <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {isAddressLinkable(d) ? (
                          <a
                            href={mapsHref(d)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline hover:text-slate-700"
                          >
                            {addr}
                          </a>
                        ) : (
                          addr
                        )}
                      </div>
                      {d.claim_number && (
                        <div className="text-xs text-slate-400 mt-0.5">#{d.claim_number}</div>
                      )}
                    </div>
                    <div className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex-shrink-0">
                      Approved
                    </div>
                  </div>

                  {/* RM / Split RM / Canvasser — view or edit */}
                  {isEditing ? (
                    <div className="bg-slate-50 rounded-lg p-3 mb-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-500 w-20">RM</label>
                        <select
                          value={editOwner}
                          onChange={(e) => setEditOwner(e.target.value)}
                          className="flex-1 text-xs border border-slate-300 rounded px-2 py-1"
                        >
                          <option value="">— select —</option>
                          {rmList.map((r) => (
                            <option key={r.id} value={r.id}>{r.full_name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-500 w-20">Split RM</label>
                        <select
                          value={editSplit}
                          onChange={(e) => setEditSplit(e.target.value)}
                          className="flex-1 text-xs border border-slate-300 rounded px-2 py-1"
                        >
                          <option value="__none__">None</option>
                          {rmList.map((r) => (
                            <option key={r.id} value={r.id}>{r.full_name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-500 w-20">Canvasser</label>
                        <input
                          type="text"
                          value={editCanvasser}
                          onChange={(e) => setEditCanvasser(e.target.value)}
                          placeholder="Canvasser name"
                          className="flex-1 text-xs border border-slate-300 rounded px-2 py-1"
                        />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={() => saveEdit(d.id)}
                          disabled={isBusy}
                          className="px-3 py-1 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-3 py-1 text-xs font-medium rounded bg-slate-200 text-slate-600 hover:bg-slate-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs">
                      <div>
                        <span className="text-slate-400">RM: </span>
                        <span className="text-bardin-ink font-medium">
                          {d.owner_id ? members[d.owner_id] ?? "—" : "—"}
                        </span>
                      </div>
                      {d.split_rm_id && (
                        <div>
                          <span className="text-slate-400">Split: </span>
                          <span className="text-bardin-ink font-medium">
                            {members[d.split_rm_id] ?? "—"}
                          </span>
                        </div>
                      )}
                      {d.insurance_company && (
                        <div>
                          <span className="text-slate-400">Insurance: </span>
                          <span className="text-bardin-ink">{d.insurance_company}</span>
                        </div>
                      )}
                      <button
                        onClick={() => {
                          setEditingId(d.id);
                          setEditOwner(d.owner_id ?? "");
                          setEditSplit(d.split_rm_id ?? "__none__");
                          setEditCanvasser("");
                        }}
                        className="text-bardin-teal hover:underline font-medium ml-auto"
                      >
                        Edit ✎
                      </button>
                    </div>
                  )}

                  {/* Convert button */}
                  <button
                    onClick={() => convertToLead(d.id)}
                    disabled={isBusy}
                    className="px-5 py-2 text-xs font-semibold rounded bg-bardin-orange text-white hover:bg-bardin-orange/90 transition disabled:opacity-50"
                  >
                    {isBusy ? "Converting…" : "Convert → Lead"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdjusterMeetingRecap — compact follow-up tile shown on each reinspection
// card so Jennifer has context on what happened at the original adjuster
// meeting (carrier adjuster present? which LA firm? what was the outcome?).
// Renders nothing if no adjuster data was captured (legacy deals stay clean).
// ---------------------------------------------------------------------------

function AdjusterMeetingRecap({ deal }: {
  deal: Pick<Deal, "adjuster_name" | "adjuster_present" | "la_company" | "adjuster_meeting_outcome">;
}) {
  const hasAnything =
    !!deal.adjuster_name?.trim()
    || deal.adjuster_present !== null && deal.adjuster_present !== undefined
    || !!deal.la_company?.trim()
    || !!deal.adjuster_meeting_outcome?.trim();
  if (!hasAnything) return null;

  return (
    <div className="mt-1.5 pl-2 border-l-2 border-slate-200">
      <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
        <span className="font-semibold uppercase tracking-wide text-slate-400">Original meeting</span>
        {deal.adjuster_name?.trim() && (
          <span className="text-slate-600">{deal.adjuster_name.trim()}</span>
        )}
        {deal.la_company?.trim() && (
          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
            LA: {deal.la_company.trim()}
          </span>
        )}
        {deal.adjuster_present === true && (
          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
            adjuster present
          </span>
        )}
        {deal.adjuster_present === false && (
          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 font-semibold">
            ⚠ adjuster no-show
          </span>
        )}
      </div>
      {deal.adjuster_meeting_outcome?.trim() && (
        <div className="text-[11px] text-slate-600 mt-0.5 whitespace-pre-wrap">
          {deal.adjuster_meeting_outcome.trim()}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reinspections — deals in docs_and_contingency / reinspection_ready / filed
// ---------------------------------------------------------------------------

function ReinspectionsTab() {
  type Row = Pick<
    Deal,
    | "id"
    | "homeowner_name"
    | "claim_number"
    | "stage"
    | "days_in_stage"
    | "stage_entered_at"
    | "phase_entered_at"
  | "phase_entered_at"
    | "created_at"
    | "reinspection_status"
    | "reinspection_reason"
    | "reinspection_round_count"
    | "reinspection_partial_approval"
    | "owner_id"
    | "hover_link"
    | "property_street"
    | "property_city"
    | "property_state"
    | "property_zip"
    | "insurance_company"
    // Original adjuster-meeting follow-up — surfaced on the card so Jennifer
    // has context on what happened the first time before filing the
    // reinspection. Apr 29 2026 add.
    | "adjuster_name"
    | "adjuster_present"
    | "la_company"
    | "adjuster_meeting_outcome"
  >;
  const [rows, setRows] = useState<Row[] | null>(null);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [sentApex, setSentApex] = useState<Set<string>>(new Set());
  // Neighborhood insurance results (Oct 27, 2026): one batch call for every
  // card. A failure only hides the lines and says so; the board still works.
  const [nbh, setNbh] = useState<Record<string, DealNeighborhoodOutcomes>>({});
  const [nbhErr, setNbhErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [{ data, error }, { data: roster }] = await Promise.all([
        supabase
          .from("deals")
          .select(
            "id,homeowner_name,claim_number,stage,days_in_stage,stage_entered_at,phase_entered_at,created_at,reinspection_status,reinspection_reason,reinspection_round_count,reinspection_partial_approval,owner_id,hover_link,property_street,property_city,property_state,property_zip,insurance_company,adjuster_name,adjuster_present,la_company,adjuster_meeting_outcome",
          )
          .in("stage", ["partial_approval", "docs_and_contingency", "reinspection_ready", "reinspection_filed"]),
        supabase.from("team_roster").select("id,full_name,email").eq("is_active", true),
      ]);
      if (error) {
        if (!cancelled) setErr(error.message);
        return;
      }
      if (!cancelled) {
        setRows((data ?? []) as Row[]);
        const m: Record<string, string> = {};
        (roster ?? []).forEach((r: Record<string, unknown>) => { m[r.id as string] = r.full_name as string; });
        setMembers(m);
      }
      try {
        const byDeal = await fetchDealNeighborhoodOutcomes(((data ?? []) as Row[]).map((r) => r.id));
        if (!cancelled) setNbh(byDeal);
      } catch (e) {
        if (!cancelled) setNbhErr(e instanceof Error ? e.message : String(e));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const byStage = useMemo(() => {
    if (!rows) return {} as Record<string, Row[]>;
    const grouped = rows.reduce((acc, r) => {
      const k = r.stage ?? "unknown";
      if (!acc[k]) acc[k] = [];
      acc[k].push(r);
      return acc;
    }, {} as Record<string, Row[]>);
    // Within each stage group, float partial approvals to the front of the
    // line (they convert better than denials), then by days in stage desc.
    for (const k of Object.keys(grouped)) {
      grouped[k].sort((a, b) => {
        const pa = a.reinspection_partial_approval ? 1 : 0;
        const pb = b.reinspection_partial_approval ? 1 : 0;
        if (pa !== pb) return pb - pa;
        return daysInStage(b) - daysInStage(a);
      });
    }
    return grouped;
  }, [rows]);

  const handleApexEmail = (d: Row) => {
    const name = d.homeowner_name ?? "Homeowner";
    const addr = [d.property_street, d.property_city, d.property_state, d.property_zip].filter(Boolean).join(", ");
    const rmName = d.owner_id ? members[d.owner_id] ?? "" : "";
    const carrier = d.insurance_company ?? "insurance carrier";
    const claim = d.claim_number ?? "";
    const hoverNote = d.hover_link
      ? `\nHover measurement link: ${d.hover_link}`
      : "\n(Hover measurement attached separately — please reach out if you don't have it)";

    const subject = `Full Roof Replacement Estimate Request — ${name} — ${claim || addr}`;
    const body = [
      `Hi Apex team,`,
      ``,
      `We need a full roof replacement estimate for the following property:`,
      ``,
      `Homeowner: ${name}`,
      `Property: ${addr}`,
      claim ? `Claim #: ${claim}` : null,
      `Insurance: ${carrier}`,
      `RM: ${rmName}`,
      hoverNote,
      ``,
      `Please prepare a full roof replacement estimate using the Hover measurements. This is for a reinspection filing with the carrier.`,
      ``,
      `Thank you,`,
      `Bardin Roofing`,
    ].filter((l) => l !== null).join("\n");

    const mailto = `mailto:supplements@apexestimators.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto, "_blank");
    setSentApex((prev) => new Set(prev).add(d.id));
  };

  if (err) return <Panel error>Couldn&apos;t load reinspections — {err}</Panel>;
  if (!rows) return <Panel>Loading reinspections…</Panel>;

  const STAGE_LABEL: Record<string, string> = {
    partial_approval: "◑ Partial approval (new — prioritize)",
    docs_and_contingency: "Docs & contingency (gathering 6 prerequisites)",
    reinspection_ready: "Ready to file (all docs in)",
    reinspection_filed: "Filed — awaiting insurance response",
  };

  return (
    <div className="space-y-4">
      {nbhErr ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Neighborhood insurance results didn&apos;t load ({nbhErr}). The cards below are complete otherwise.
        </div>
      ) : null}
      {Object.entries(STAGE_LABEL).map(([k, label]) => {
        const list = byStage[k] ?? [];
        return (
          <Panel key={k}>
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-sm font-semibold text-bardin-ink">{label}</h3>
              <span className="text-xs text-slate-500">{fmtInt(list.length)}</span>
            </div>
            {list.length === 0 ? (
              <Empty>Nothing here.</Empty>
            ) : (
              <ul className="divide-y divide-slate-100">
                {list.map((d) => (
                  <li key={d.id} className="py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-bardin-ink flex items-center gap-2">
                        <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                        {d.reinspection_partial_approval && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                            ◑ Partial
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <DealAgeChips deal={d} align="right" />
                        {d.reinspection_round_count && d.reinspection_round_count > 1 ? (
                          <div className="text-xs text-slate-500">round {d.reinspection_round_count}</div>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="text-xs text-slate-500">
                        {d.claim_number && <span className="mr-2">{d.claim_number}</span>}
                        {d.insurance_company && <span className="text-slate-400">· {d.insurance_company}</span>}
                        {d.reinspection_reason && (
                          <span className="italic ml-2">— {d.reinspection_reason}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {d.hover_link && (
                          <a
                            href={d.hover_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] text-bardin-teal hover:underline"
                          >
                            Hover →
                          </a>
                        )}
                        <button
                          onClick={() => handleApexEmail(d)}
                          className={`text-[10px] font-medium px-2 py-0.5 rounded transition ${
                            sentApex.has(d.id)
                              ? "bg-green-100 text-green-700"
                              : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                          }`}
                        >
                          {sentApex.has(d.id) ? "✓ Apex Sent" : "Send Apex FRR Request"}
                        </button>
                      </div>
                    </div>

                    {/* Original adjuster meeting follow-up — context for why
                        this deal is now in reinspection. Renders nothing if
                        no adjuster info was captured (legacy deals). */}
                    <AdjusterMeetingRecap deal={d} />

                    {/* How this carrier's claims went nearby: approved at the
                        first meeting, and denials won on reinspection. */}
                    <NeighborhoodInsuranceLine outcome={nbh[d.id]} />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        );
      })}
    </div>
  );
}

// (Warranty tab moved to dispatch/WarrantyTab.tsx on May 9, 2026 — Robert +
// Jennifer drive warranty work from the Dispatch Board now.)
// ---------------------------------------------------------------------------
// SLA Dashboard — Jeremy 72hr + Jennifer 24hr hit rates
// ---------------------------------------------------------------------------

function SLATab() {
  type Row = Pick<Deal, "id" | "report_sla_met_jeremy" | "qc_sla_met_jennifer">;
  const [rows, setRows] = useState<Row[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase
        .from("deals")
        .select("id,report_sla_met_jeremy,qc_sla_met_jennifer")
        .not("drone_completed_date", "is", null);
      if (error) {
        if (!cancelled) setErr(error.message);
        return;
      }
      if (!cancelled) setRows((data ?? []) as unknown as Row[]);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) return <Panel error>Couldn&apos;t load SLA — {err}</Panel>;
  if (!rows) return <Panel>Loading SLA…</Panel>;

  const jeremyTotal = rows.filter((r) => r.report_sla_met_jeremy != null).length;
  const jeremyHits = rows.filter((r) => r.report_sla_met_jeremy === true).length;
  const jenniferTotal = rows.filter((r) => r.qc_sla_met_jennifer != null).length;
  const jenniferHits = rows.filter((r) => r.qc_sla_met_jennifer === true).length;

  return (
    <div className="grid grid-cols-2 gap-3">
      <Kpi
        label="Jeremy 72-hr SLA"
        value={jeremyTotal ? fmtPct(jeremyHits / jeremyTotal, 1) : "—"}
        sub={`${fmtInt(jeremyHits)} / ${fmtInt(jeremyTotal)} reports on time`}
        valueClass={
          jeremyTotal && jeremyHits / jeremyTotal >= 0.9
            ? "text-emerald-600"
            : "text-amber-600"
        }
      />
      <Kpi
        label="Jennifer 24-hr QC"
        value={jenniferTotal ? fmtPct(jenniferHits / jenniferTotal, 1) : "—"}
        sub={`${fmtInt(jenniferHits)} / ${fmtInt(jenniferTotal)} QC'd on time`}
        valueClass={
          jenniferTotal && jenniferHits / jenniferTotal >= 0.9
            ? "text-emerald-600"
            : "text-amber-600"
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function Panel({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div className={error ? "bg-red-50 text-red-700 p-4 rounded" : "bg-white rounded-xl shadow p-5"}>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-slate-500 py-2">{children}</div>;
}

function Kpi({
  label,
  value,
  sub,
  valueClass,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={"text-2xl font-semibold mt-1 " + (valueClass ?? "text-bardin-ink")}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
